/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ProgramSize} from '../../model/programSize';
import {WorkloadTimingSize} from '../../model/workloadTimingSize';
import {CellEditingStoppedEvent, ColDef} from 'ag-grid-community';
import {ActionsCellRendererComponent} from '../cell-renderer/actions-cell-renderer.component';
import {EditableGridComponent} from '../editable-grid-component';
import {
    CustomMultiSelectFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-filter.component';
import {
    CustomMultiSelectFloatingFilterComponent
} from '../filter-components/custom-multi-select-filter/custom-multi-select-floating-filter.component';
import {TypeaheadCellEditorComponent} from '../cell-renderer/typeahead-cell-editor.component';
import {CommonHelperService} from '../../service/common-helper.service';
import {Workload} from '../../model/workload';
import {ChartData} from '../../model/chartData';
import {refreshCellAndDeferredRowRedraw} from '../cell-renderer/prime-cell-editor-tab.util';

@Component({
    selector: 'app-workload-timing',
    templateUrl: './workload-timing.component.html',
    styleUrls: ['./workload-timing.component.scss'],
    standalone: false
})
export class WorkloadTimingComponent extends EditableGridComponent implements OnInit {
  @ViewChild('workloadGrid', { read: ElementRef }) private workloadGridHost?: ElementRef<HTMLElement>;

  permissionUrl = '/esciUI/workloadTimingPermission';
  saveUrl = '/esciUI/saveProgramSize';
  rowData = new Array<ProgramSize>;
  workloadTimingSizes = new Array<WorkloadTimingSize>;
  fullWorkloadList = new Array<Workload>;
  chartData = new Array<ChartData>;
  activeTabIndex = 0;
  readonly hasChartData = computed(() => this.rowDataSignal().some(o => !!o.size));

  get safeActiveTabIndex(): number {
    return this.activeTabIndex === 1 ? 1 : 0;
  }

  onTabChange(value: number): void {
    const next = Number(value);
    this.activeTabIndex = next === 1 ? 1 : 0;
    this.calculateFullWorkloadList();
  }

  cols: ColDef[] = [
    {minWidth: 130, maxWidth: 130, headerName: 'Actions', colId: 'actions', editable: false,
      filter: false, sortable: false, lockPosition: true, suppressMovable: true,
      headerCheckboxSelection: true, checkboxSelection: true,
      cellRenderer: ActionsCellRendererComponent,
      cellRendererParams: {
        onDeleteClick: (function (params: any): void {
          this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
        }).bind(this),
        hideDeleteButtonIfFieldExists: 'momentumId',
        onCopyClick: this.onCopyClick.bind(this),
        onRevertClick: this.onRevertClick.bind(this),
        fieldForDeleteMessage: 'programName'
      }
    },
    {field: 'id', headerName: 'ID', editable: false, maxWidth: 100, hide: true, sortable: false},
    {field: 'programName', headerName: 'Program Name', minWidth: 400, editable: (params) => !params.data.momentumId},
    {field: 'momentumId', headerName: 'Momentum ID', editable: false, hide: true},
    {field: 'fromMomentum', headerName: 'From Momentum', editable: false,
      filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
      valueGetter: (params) => params.data.momentumId ? 'Yes' : 'No'},
    {field: 'size', headerName: 'Size', filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
      cellEditor: TypeaheadCellEditorComponent, cellEditorParams: {values: ProgramSize.sizes, editable: false}, maxWidth: 200},
    {field: 'region', headerName: 'Region', filter: CustomMultiSelectFilterComponent, floatingFilterComponent: CustomMultiSelectFloatingFilterComponent,
      cellEditor: TypeaheadCellEditorComponent, cellEditorParams: {values: ProgramSize.regions, editable: false, maxWidth: 200}},
    {field: 'sorpDate', headerName: 'SORP Date', cellEditor: 'agDateCellEditor',
      valueGetter: (params) => {
        const date = new Date(params.data.sorpDate);
        return isNaN(date.getTime()) ? null : date;
      },
      cellDataType: 'date', editable: (params) => !params.data.momentumId},
    {field: 'createdDate', headerName: 'Created Date', cellDataType: 'date', editable: false, hide: true},
    {field: 'createdBy', headerName: 'Created By', editable: false, hide: true},
  ];
  ngOnInit(): void {
    this.loading = true;
    this.esciService.getPermissionAndFileSizeLimit(this);
    this.esciService.getWorkloadTimingSize(this);
    this.esciService.getProgramSize(this, ()=>{
      this.loading = false;
    });
    this.cols.forEach(col => {
      col.tooltipValueGetter = (params) => {
        return params.data.changes.get(col.field);
      }
    });
  }

  saveProgramSizes() {
      if (this.areRowsValid(this.rowData)) {
        this.setRowData(this.rowData.filter(row => row.state !== 'deleted'));
        // the dropdown does not allow nulls anymore so convert empty strings to nulls for size
        this.rowData.forEach(row => {
            if (row.size === '') {
                row.size = null;
            }
        });
        this.esciService.saveRows(this, this.rowData, 'Workload Timing', () => {
          this.esciService.getProgramSize(this, () => {
            this.loading = false;
            this.refreshGrid(this.rowData);
            this.setFilter();
          });
        });
      }
  }

  addRow() {
    // add a row to the grid
    const newRow = new ProgramSize();
    // default to 2023 so it shows in the grid
    newRow.sorpDate = new Date('2023-01-02');
    newRow.originalRowData = structuredClone(newRow);
    newRow.changes.clear();
    newRow.id = -1;
    newRow.state = 'new';
    this.rowData.splice(0, 0, newRow);
    this.dataChanged = true;
    this.setFilter();
    this.refreshGrid(this.rowData);
  }

  onCopyClick(params): void {
    // find the index of the row clicked on
    let index = this.rowData.indexOf(params.data);
    if (index === -1) {
      // Can this happen?
      index = this.rowData.findIndex(row => row['id'] === params.data.id);
    }
    if (index >= 0) {
      // copy the row and add it to the array at the index
      const newRow = structuredClone(params.data);
      newRow.id = -1;
      newRow.momentumId = null;
      newRow.state = 'new';
      this.rowData.splice(index + 1, 0, newRow);
      // update the grid
      this.refreshGrid(this.rowData);
    }
  }

  cellEditingStopped(params: CellEditingStoppedEvent<any>) {
    // bulk update region or size on all selected rows
    const field = params.column.getColId(); // Get the edited column
    if (field === 'size' || field === 'region') {
      const selectedNodes = params.api.getSelectedNodes(); // Get selected rows
      const newValue = params.value; // Get the new value
      selectedNodes.forEach(node => {
        node.setDataValue(field, newValue); // Apply change to all selected rows
      });
    }
    this.updateDataChanged(this.rowData);
    const api = params.api;
    const nodes =
      field === 'size' || field === 'region'
        ? params.api.getSelectedNodes()
        : params.node
          ? [params.node]
          : [];
    if (nodes.length && api && !api.isDestroyed() && params.column) {
      for (const n of nodes) {
        refreshCellAndDeferredRowRedraw(api, n, params.column);
      }
    }
  }

  resetChanges() {
    this.loading = true;
    this.resetting = true;
    this.messageService.clear();
    this.esciService.getProgramSize(this, ()=>{
      this.resetting = false;
      this.loading = false;
      this.refreshGrid(this.rowData);
    });
  }

  exportReport() {
    // export all data in the grid
    this.excelService.exportGridDataAsExcelFile(this.gridApi, 'Workload_Timing');
  }

  calculateFullWorkloadList() {
    if (this.activeTabIndex === 1) {
      this.fullWorkloadList.length = 0;
      // we will combine the workload timing size data and program size data here
      this.rowData.forEach(program => {
        if (program.size) {
          this.workloadTimingSizes.filter(w => w.size === program.size).forEach(size => {
            // gather all the workload values for this size and put it into the map based on the sorp date and offsets etc
            const date = CommonHelperService.calculateDateWithOffset(program.sorpDate + '', size.offsetFromSORP);
            const workload = new Workload(date, size.workload, size.role, program.region, program.programName, size.milestoneName);
            this.fullWorkloadList.push(workload);
          })
        }
      });
      this.convertWorkloadListToChartData();
    }
  }

  convertWorkloadListToChartData() {
    // take fullWorkloadList and group by dates into quarters
    const quarterMap = new Map<string, ChartData>();
    this.fullWorkloadList.forEach((workload) => {
      if (workload.date && isNaN(workload.date.getTime()) === false) {
        const quarter = this.getQuarter(workload.date);
        const region = workload.region || 'N/A';
        const key = `${quarter}-${workload.role}-${region}-${workload.programName}`;

        if (!quarterMap.has(key)) {
          quarterMap.set(key, {
            label: quarter,
            role: workload.role,
            region: region,
            project: workload.programName,
            workload: 0
          });
        }
        quarterMap.get(key)!.workload += workload.workload;
      }
    });

    this.chartData = Array.from(quarterMap.values());
    this.chartData.sort((a, b) => a.role.localeCompare(b.role));
    this.chartData.sort((a, b) => a.region.localeCompare(b.region));
    // compare quarters must come last so the dropdown in the chart will be in order
    this.chartData.sort((a, b) => this.compareQuarters(a.label, b.label));
  }

  // Sorting helper function
  compareQuarters(a: string, b: string): number {
    const [qA, yearA] = a.split(' ').map(s => s.trim());
    const [qB, yearB] = b.split(' ').map(s => s.trim());

    // Convert "Q1", "Q2", etc. to numbers
    const quarterA = parseInt(qA.substring(1), 10);
    const quarterB = parseInt(qB.substring(1), 10);
    const yearNumA = parseInt(yearA, 10);
    const yearNumB = parseInt(yearB, 10);

    // First, sort by year, then by quarter
    return yearNumA - yearNumB || quarterA - quarterB;
  }

  // Helper function to get the quarter label (e.g., "Q1 2024")
  getQuarter(date: Date): string {
    const year = date.getFullYear();
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    return `Q${quarter} ${year}`;
  }

  onRevertClick(params: any): void {
    const row = params.data;
    if (row.state === 'new') {
      return this.onDeleteClick(params, this.rowData, (updatedData) => this.refreshGrid(updatedData));
    }
    for (const col of this.cols) {
      if (!col.field) {
        continue;
      }
      const originalValue = CommonHelperService.getDeepValue(row.originalRowData, col.field);
      CommonHelperService.setDeepValue(row, col.field, originalValue);
    }
    params.data.state = 'saved';
    // update the grid
    this.refreshGrid(this.rowData);
  }

  // Default the SORP Filter to only values 2023 or higher
  setFilter() {
    if (!this.isGridApiAlive(this.gridApi)) {
      return;
    }
    const formattedDate = new Date('2023-01-01').toISOString().substring(0, 10);
    this.gridApi.setFilterModel({
      sorpDate: {
        type: 'greaterThan',
        dateFrom: formattedDate
      }
    });
    this.gridApi.onFilterChanged();
  }


  onWorkloadGridReady(params) {
    this.onGridReady(params);
    this.setFilter();
    // get latest date from all updatedDates
    const latestDate = this.rowData.map(p => p.updatedDate).reduce((a, b) => a > b ? a : b);
    if (latestDate) {
      const formattedDate = latestDate.toLocaleString('en-US', {
        timeZoneName: 'short',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).replace(',', '');
      this.addCustomFooterToAgGrid('Last Momentum Update:', formattedDate, this.workloadGridHost?.nativeElement);
    }
  }
}
